(function () {
  'use strict';

  if (customElements.get('complete-your-set')) return;
  function formatMoney(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return String(window.theme.formatMoney(cents, window.theme.moneyFormat));
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function toCents(dollarString) {
    return Math.round(parseFloat(dollarString || '0') * 100);
  }

  function toHandle(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function deriveColorsFromHandles(handles) {
    const tokenLists = handles.map((h) => (h || '').split('-').filter(Boolean));
    if (tokenLists.length < 2) return handles.map(() => '');

    let commonLength = tokenLists[0].length;
    for (let i = 1; i < tokenLists.length; i++) {
      let j = 0;
      while (j < commonLength && j < tokenLists[i].length && tokenLists[i][j] === tokenLists[0][j]) {
        j++;
      }
      commonLength = j;
    }

    return tokenLists.map((tokens) => {
      const rest = tokens.slice(commonLength);
      if (rest.length === 0) return '';
      return rest.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    });
  }

  class CompleteYourSet extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('[data-cys-checkbox]');
      this.checkboxLabel = this.querySelector('[data-cys-checkbox-label]');
      this.optionRow = this.querySelector('[data-cys-option]');
      this.swatchList = this.querySelector('[data-cys-swatches]');
      this.titleEl = this.querySelector('[data-cys-title]');
      this.descEl = this.querySelector('[data-cys-desc]');
      this.priceEl = this.querySelector('[data-cys-price]');
      this.comparePriceEl = this.querySelector('[data-cys-compare-price]');
      this.badgeEl = this.querySelector('[data-cys-badge]');
      this.optionValueEl = this.querySelector('[data-cys-option-value]');
      this.hiddenVariantInput = this.querySelector('[data-cys-variant-id]');

      this.badgeTemplate = this.getAttribute('data-badge-template') || 'Save [discount]';
      this.descriptionTemplate =
        this.getAttribute('data-description-template') || 'Complete your set with the matching [product].';
      this.collectionHandle = this.getAttribute('data-collection-handle');
      this.colorLabel = this.getAttribute('data-color-label') || 'Color';
      this.currentColor = this.getAttribute('data-default-color') || '';
      this.showSwatches = this.getAttribute('data-show-swatches') === 'true';
      this.showSwatchImages = this.getAttribute('data-swatch-images') !== 'false';
      this.swatchShape = this.getAttribute('data-swatch-shape') === 'circle' ? 'circle' : 'square';
      this.showAvailableOnly = this.getAttribute('data-show-available-only') === 'true';
      this.defaultProductId = this.getAttribute('data-default-product-id');
      this.colorsById = this.parseColorMap();

      // Bundle pricing: the set price is a fixed total; the add-on's shown price
      // is always (set price - current product price), so it only depends on
      // this PDP's variant, never on which add-on swatch is selected.
      this.setPriceCents = parseInt(this.getAttribute('data-set-price'), 10) || 0;
      this.productPriceCents = parseInt(this.getAttribute('data-product-price'), 10) || 0;
      this.productFullPriceCents = parseInt(this.getAttribute('data-product-full-price'), 10) || 0;
      this.currentAddonFullPriceCents = parseInt(this.getAttribute('data-default-addon-full-price'), 10) || 0;

      this.products = [];

      this.formSelector = this.getAttribute('data-form-selector') || '[data-product-form]';
      this.cartAddUrl =
        (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add';

      this.bindCheckbox();
      this.bindMainForm();
      this.bindSwatchClicks();
      this.updateCta();

      document.addEventListener('theme:variant:change', (event) => this.handleVariantChange(event));

      if (this.showSwatches && this.swatchList && this.collectionHandle) {
        this.loadSwatches();
      }
    }

    parseColorMap() {
      const map = {};
      try {
        const ids = JSON.parse(this.getAttribute('data-swatch-ids') || '[]');
        ids.forEach((id, i) => {
          if (i > 11) return;
          const color = this.getAttribute('data-swatch-color-' + i);
          if (color) map[String(id)] = color;
        });
      } catch (e) {}
      return map;
    }

    bindSwatchClicks() {
      if (!this.swatchList) return;
      this.swatchList.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-cys-swatch-btn]');
        if (!btn || btn.hasAttribute('data-sold-out')) return;
        event.preventDefault();
        this.selectProduct(btn.getAttribute('data-product-id'));
      });
    }

    loadSwatches() {
      const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
      fetch(root + 'collections/' + this.collectionHandle + '/products.json?limit=24')
        .then((res) => res.json())
        .then((data) => {
          const items = (data && data.products) || [];
          const colorsFromHandles = deriveColorsFromHandles(items.map((p) => p.handle));

          this.products = items.map((product, i) => {
            const variant = product.variants && product.variants[0];
            return {
              product_id: product.id,
              variant_id: variant ? variant.id : null,
              title: product.title,
              color: this.colorsById[String(product.id)] || colorsFromHandles[i] || product.title,
              image: product.images && product.images[0] ? product.images[0].src : '',
              price: variant ? toCents(variant.price) : 0,
              compare_at_price: variant ? toCents(variant.compare_at_price) : 0,
              available: variant ? variant.available : false,
            };
          });

          if (this.products.length > 1) {
            this.renderSwatches();
            if (this.optionRow) this.optionRow.hidden = false;
          }
        })
        .catch(() => {});
    }

    renderSwatches() {
      this.swatchList.innerHTML = '';

      this.products.forEach((product) => {
        const isSelected = String(product.product_id) === String(this.defaultProductId);

        const li = document.createElement('li');
        li.className = 'cys__swatch';
        li.setAttribute('data-cys-swatch', '');
        li.setAttribute('data-product-id', product.product_id);
        li.setAttribute('data-selected', String(isSelected));

        if (this.showSwatchImages) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cys__swatch-btn';
          btn.setAttribute('data-cys-swatch-btn', '');
          btn.setAttribute('data-product-id', product.product_id);
          btn.setAttribute('aria-label', product.color);
          if (!product.available) btn.setAttribute('data-sold-out', 'true');

          const img = document.createElement('img');
          img.src = product.image;
          img.alt = product.title;
          img.loading = 'lazy';
          btn.appendChild(img);

          li.appendChild(btn);
        } else {
          // Mirrors the exact markup/classes snippets/product-siblings.liquid uses for
          // the main product's own colour swatches, so both render identically off
          // the same --{{ handle }} custom properties and --swatch-size scope.
          const handle = toHandle(product.color);

          const wrap = document.createElement('tooltip-component');
          wrap.className = 'swatches swatch__button swatch__button--' + this.swatchShape + ' swatch-' + handle;
          wrap.style.setProperty('--swatch', 'var(--' + handle + ')');
          wrap.setAttribute('data-tooltip', product.color);

          const btn = document.createElement('button');
          btn.type = 'button';
          let linkClass = 'sibling__link';
          if (isSelected) linkClass += ' sibling__link--current';
          if (!product.available) linkClass += ' sibling__link--sold-out';
          btn.className = linkClass;
          btn.setAttribute('data-cys-swatch-btn', '');
          btn.setAttribute('data-product-id', product.product_id);
          btn.setAttribute('aria-label', product.color);
          if (!product.available) btn.setAttribute('data-sold-out', 'true');

          const hiddenText = document.createElement('span');
          hiddenText.className = 'visually-hidden';
          hiddenText.textContent = product.color;
          btn.appendChild(hiddenText);

          wrap.appendChild(btn);
          li.appendChild(wrap);
        }

        this.swatchList.appendChild(li);
      });
    }

    markSwatchSelected(productId) {
      if (!this.swatchList) return;
      this.swatchList.querySelectorAll('[data-cys-swatch]').forEach((li) => {
        const isSelected = li.getAttribute('data-product-id') === String(productId);
        li.setAttribute('data-selected', String(isSelected));

        const btn = li.querySelector('[data-cys-swatch-btn]');
        if (btn && btn.classList.contains('sibling__link')) {
          btn.classList.toggle('sibling__link--current', isSelected);
        }
      });
    }

    bindCheckbox() {
      if (!this.checkbox) return;
      this.setActive(this.checkbox.checked);
      this.checkbox.addEventListener('change', () => {
        this.setActive(this.checkbox.checked);
        this.updateCta();
      });
    }

    setActive(isActive) {
      this.setAttribute('data-active', isActive ? 'true' : 'false');
    }

    selectProduct(productId) {
      const product = this.products.find((p) => String(p.product_id) === String(productId));
      if (!product) return;

      this.markSwatchSelected(productId);

      if (this.hiddenVariantInput) this.hiddenVariantInput.value = product.variant_id;
      this.currentColor = product.color;
      if (this.optionValueEl) this.optionValueEl.textContent = product.color;
      if (this.titleEl) this.titleEl.textContent = product.title;
      if (this.checkboxLabel) this.checkboxLabel.textContent = 'Add ' + product.title + ' to your order';
      if (this.descEl) this.descEl.textContent = this.descriptionTemplate.replace('[product]', product.title);

      this.currentAddonFullPriceCents =
        product.compare_at_price > product.price ? product.compare_at_price : product.price;

      this.renderPricing();

      this.dispatchEvent(
        new CustomEvent('cys:product-change', { bubbles: true, detail: { product } })
      );
    }

    // Recomputes the add-on price line, the crossed-out full price, and the
    // saving badge from the current product/set prices, then refreshes the
    // Add to Cart button total. Always displayed (per spec), never hidden
    // behind a "has discount" check.
    renderPricing() {
      const addonDisplayPrice = Math.max(0, this.setPriceCents - this.productPriceCents);
      const saving = Math.max(
        0,
        this.productFullPriceCents + this.currentAddonFullPriceCents - this.setPriceCents
      );

      if (this.priceEl) this.priceEl.innerHTML = formatMoney(addonDisplayPrice);
      if (this.comparePriceEl) this.comparePriceEl.innerHTML = formatMoney(this.currentAddonFullPriceCents);

      if (this.badgeEl) {
        this.badgeEl.innerHTML = this.badgeTemplate.replace('[discount]', formatMoney(saving));
      }

      this.updateCta();
    }

    // Keeps this product's price in sync if the shopper changes the main
    // variant picker (size/colour) while this block is on the page. The set
    // price itself never changes; only the add-on split depends on it.
    handleVariantChange(event) {
      const variant = event && event.detail && event.detail.variant;
      if (!variant || typeof variant.price !== 'number') return;

      this.productPriceCents = variant.price;
      this.productFullPriceCents =
        variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;

      this.renderPricing();
    }

    // While the add-on is checked, the Add to Cart button (and its sticky
    // clone, if present) show the fixed bundle total crossed against the
    // combined full price of both items — same pattern as the CYS card's own
    // price row. Unchecked, it's just this product's own price/compare.
    updateCta() {
      const isActive = !!(this.checkbox && this.checkbox.checked);
      const total = isActive ? this.setPriceCents : this.productPriceCents;
      const compareTotal = isActive
        ? this.productFullPriceCents + this.currentAddonFullPriceCents
        : this.productFullPriceCents;
      const showCompare = compareTotal > total;

      if (this.addButton) {
        const currentEl = this.addButton.querySelector('.price__current');
        const compareEl = this.addButton.querySelector('.price__compare');
        if (currentEl) currentEl.innerHTML = formatMoney(total);
        if (compareEl) {
          compareEl.hidden = !showCompare;
          if (showCompare) compareEl.innerHTML = formatMoney(compareTotal);
        }
      }

      const stickyPrice = document.querySelector('.sticky-price');
      const stickyComparePrice = document.querySelector('.sticky-compare-price');
      if (stickyPrice) stickyPrice.innerHTML = formatMoney(total);
      if (stickyComparePrice) {
        stickyComparePrice.style.display = showCompare ? 'inline' : 'none';
        if (showCompare) stickyComparePrice.innerHTML = formatMoney(compareTotal);
      }
    }

    bindMainForm() {
      this.form = document.querySelector(this.formSelector);
      if (!this.form) return;

      this.addButton = this.form.querySelector('[data-add-to-cart], button[type="submit"]');
      if (!this.addButton) return;

      let bypass = false;

      document.addEventListener(
        'click',
        (event) => {
          if (bypass) return;
          if (event.target.closest('[data-add-to-cart], button[type="submit"]') !== this.addButton) return;
          if (!this.checkbox || !this.checkbox.checked || !this.hiddenVariantInput) return;

          const variantId = this.hiddenVariantInput.value;
          if (!variantId) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          this.addAddonToCart(variantId).then((ok) => {
            if (!ok) return;
            bypass = true;
            this.addButton.click();
            bypass = false;
          });
        },
        true
      );
    }

    addAddonToCart(variantId) {
      this.clearCartError();

      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');
      if (this.currentColor) {
        body.append('properties[' + this.colorLabel + ']', this.currentColor);
      }

      return fetch(this.cartAddUrl, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body,
      })
        .then((res) => res.json().then((data) => ({ ok: res.ok, data: data })).catch(() => ({ ok: res.ok, data: null })))
        .then(({ ok, data }) => {
          if (ok) return true;
          const message = (data && (data.description || data.message)) || null;
          this.showCartError(message);
          return false;
        })
        .catch(() => {
          this.showCartError(null);
          return false;
        });
    }

    showCartError(message) {
      const container = this.form && this.form.querySelector('[data-cart-errors-container]');
      if (!container) return;

      const text = message || 'The add-on could not be added to your cart. Please try again.';

      container.innerHTML =
        '<div class="errors">' +
        text.replace(/</g, '&lt;') +
        '<button type="button" class="errors__close" data-close-error aria-label="Dismiss">×</button></div>';
      container.classList.add('is-visible');

      const closeBtn = container.querySelector('[data-close-error]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.clearCartError(), { once: true });
      }
    }

    clearCartError() {
      const container = this.form && this.form.querySelector('[data-cart-errors-container]');
      if (!container) return;
      container.classList.remove('is-visible');
      container.innerHTML = '';
    }
  }

  customElements.define('complete-your-set', CompleteYourSet);
})();
